import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { euro, dateIt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Receipt, CheckCircle2, Undo2, AlertTriangle } from "lucide-react";

import { confermaSeDuplicato } from "@/lib/duplicati";

const today = () => new Date().toISOString().slice(0, 10);

export function FattureEmesse({ commessaId, importoCommessa, clienteId }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ numero: "", data: today(), importo: "", note: "" });
  const { data: rows = [] } = useQuery({
    queryKey: ["fatture-emesse", commessaId],
    queryFn: async () => (await api.get(`/commesse/${commessaId}/fatture-emesse`)).data,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["fatture-emesse", commessaId] });
  const addMut = useMutation({
    mutationFn: async (p) => (await api.post(`/commesse/${commessaId}/fatture-emesse`, p)).data,
    onSuccess: () => { refresh(); setForm({ numero: "", data: today(), importo: "", note: "" }); toast.success("Fattura registrata"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const incMut = useMutation({
    mutationFn: async ({ id, data_incasso }) => (await api.put(`/fatture-emesse/${id}/incasso`, { data_incasso })).data,
    onSuccess: () => { refresh(); toast.success("Incasso aggiornato"); },
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/fatture-emesse/${id}`)).data,
    onSuccess: () => { refresh(); toast.success("Fattura eliminata"); },
  });

  const emesso = rows.reduce((s, r) => s + r.importo, 0);
  const incassato = rows.filter((r) => r.data_incasso).reduce((s, r) => s + r.importo, 0);
  const percFatturato = importoCommessa > 0 ? (emesso / importoCommessa) * 100 : 0;
  const nuovoImporto = Number(form.importo) || 0;
  const sfondamento = nuovoImporto > 0 && importoCommessa > 0 && emesso + nuovoImporto > importoCommessa + 0.005;
  const [confermaSfondamento, setConfermaSfondamento] = useState(false);
  const aggiungi = async () => {
    if (!form.numero || !form.importo) return toast.error("Inserisci numero e importo");
    if (sfondamento && !confermaSfondamento) return setConfermaSfondamento(true);
    setConfermaSfondamento(false);
    if (!(await confermaSeDuplicato({ numero: form.numero, cliente_id: clienteId }))) return;
    addMut.mutate({ ...form, importo: Number(form.importo) });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          ["Importo commessa", euro(importoCommessa), "text-slate-900", "fe-kpi-commessa"],
          ["Fatturato", euro(emesso), "text-primary", "fe-kpi-emesso"],
          ["Da fatturare", euro(importoCommessa - emesso), importoCommessa - emesso < 0 ? "text-red-600" : "text-sky-700", "fe-kpi-da-fatturare"],
          ["Incassato", euro(incassato), "text-emerald-700", "fe-kpi-incassato"],
          ["Da incassare", euro(emesso - incassato), "text-amber-700", "fe-kpi-residuo"],
        ].map(([l, v, c, tid]) => (
          <div key={l} className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3" data-testid={tid}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{l}</div>
            <div className={`font-mono font-semibold mt-0.5 ${c}`}>{v}</div>
          </div>
        ))}
      </div>

      <div data-testid="fe-avanzamento">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
          <span>Avanzamento fatturazione</span>
          <span className="font-mono font-semibold text-slate-700" data-testid="fe-avanzamento-perc">{percFatturato.toFixed(1).replace(".", ",")}% fatturato</span>
        </div>
        <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full rounded-full transition-[width] duration-500 ${percFatturato > 100.05 ? "bg-red-500" : percFatturato >= 99.95 ? "bg-emerald-500" : "bg-primary"}`} style={{ width: `${Math.min(percFatturato, 100)}%` }} />
        </div>
      </div>

      {sfondamento && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="fe-avviso-sfondamento">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            Attenzione: con questa fattura il fatturato arriverebbe a <b className="font-mono">{euro(emesso + nuovoImporto)}</b>, superando l'importo commessa di <b className="font-mono">{euro(emesso + nuovoImporto - importoCommessa)}</b>.
            {confermaSfondamento && <> Premi di nuovo <b>Aggiungi</b> per confermare.</>}
          </span>
        </div>
      )}

      <div className="grid sm:grid-cols-6 gap-3 items-end">
        <div className="space-y-1.5"><Label>N. fattura *</Label><Input value={form.numero} onChange={(e) => setForm((f) => ({ ...f, numero: e.target.value }))} data-testid="fe-numero-input" /></div>
        <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={form.data} onChange={(e) => setForm((f) => ({ ...f, data: e.target.value }))} data-testid="fe-data-input" /></div>
        <div className="space-y-1.5"><Label>Importo (€) *</Label><Input type="number" step="0.01" value={form.importo} onChange={(e) => { setConfermaSfondamento(false); setForm((f) => ({ ...f, importo: e.target.value })); }} className={sfondamento ? "border-red-400 focus-visible:ring-red-400" : ""} data-testid="fe-importo-input" /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Note</Label><Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="es. Acconto 30%" data-testid="fe-note-input" /></div>
        <Button onClick={aggiungi} disabled={addMut.isPending} className={confermaSfondamento ? "bg-red-600 hover:bg-red-700" : ""} data-testid="fe-add-button"><Plus className="h-4 w-4 mr-1" /> {confermaSfondamento ? "Conferma comunque" : "Aggiungi"}</Button>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-10 text-slate-400 border border-dashed border-slate-200 rounded-lg" data-testid="fe-empty"><Receipt className="h-8 w-8 mx-auto mb-2 opacity-40" />Nessuna fattura emessa.</div>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100"><th className="py-2 px-3">Data</th><th className="py-2 px-3">N.</th><th className="py-2 px-3">Note</th><th className="py-2 px-3 text-right">Importo</th><th className="py-2 px-3">Incasso</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-50" data-testid={`fe-row-${r.id}`}>
                <td className="py-2.5 px-3">{dateIt(r.data)}</td>
                <td className="py-2.5 px-3 font-mono text-xs">{r.numero}</td>
                <td className="py-2.5 px-3 text-slate-600">{r.note}</td>
                <td className="py-2.5 px-3 text-right font-mono font-medium">{euro(r.importo)}</td>
                <td className="py-2.5 px-3">
                  {r.data_incasso ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> {dateIt(r.data_incasso)}
                      <button onClick={() => incMut.mutate({ id: r.id, data_incasso: null })} className="ml-1 text-slate-300 hover:text-slate-600" title="Annulla incasso" data-testid={`fe-annulla-incasso-${r.id}`}><Undo2 className="h-3.5 w-3.5" /></button>
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => incMut.mutate({ id: r.id, data_incasso: today() })} data-testid={`fe-incassa-${r.id}`}>Segna incassata</Button>
                  )}
                </td>
                <td className="py-2.5 px-3 text-right"><button onClick={() => delMut.mutate(r.id)} className="text-slate-300 hover:text-red-600" data-testid={`fe-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
